/*
 **	WebSocket Device Data Hook for Mobile App
 **	OMNI IQ Mobile (c) 2025 Silver Falls Capital. All Rights Reserved
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { webSocketService, type DeviceDataFromWebSocket } from '../services/webSocketService'
import { useAuth } from './useAuth'
import { useDataStore } from '../store/dataStore'

export interface RealtimeDeviceData {
  deviceId: string
  timestamp?: string
  
  // Primary data fields
  dp0?: number
  dp1?: number
  
  // Threshold configuration  
  dp0wt?: number
  dp1wt?: number
  dp0at?: number
  dp1at?: number
  
  // Alarm states
  dp0_alarm_state?: number
  dp1_alarm_state?: number
  
  // Connection status
  w_rssi?: number
  c_rssi?: number
  w_connected?: number
  c_connected?: number
  
  // Power status
  on_batt?: number
  on_power?: number
  batt_percent?: number
  
  // Device configuration
  zone1_name?: string
  zone2_name?: string
  measure_unit?: number
  measure_sec?: number
  auto_z_sec?: number
  uptime_sec?: number
  
  // Bridge connectivity
  br_ping_ct?: number
  br_bad_ping_ct?: number
  br_bad_ping_seq_ct?: number
  br_longest_bad_ping_seq_ct?: number
  
  rawData?: any
}

export interface UseWebSocketDeviceDataReturn {
  deviceData: Map<string, RealtimeDeviceData>
  isConnected: boolean
  isConnecting: boolean
  error: string | null
  connectionStatus: boolean
  
  // Methods
  connect: () => void
  disconnect: () => void
  getDeviceData: (deviceId: string) => RealtimeDeviceData | undefined
}


export const useWebSocketDeviceData = (): UseWebSocketDeviceDataReturn => {
  console.log('📡 🚀 useWebSocketDeviceData hook called/mounted')

  const { getAccessToken, user } = useAuth()
  const { updateRealtimeDeviceData } = useDataStore()
  const [deviceData, setDeviceData] = useState<Map<string, RealtimeDeviceData>>(new Map())
  const [isConnected, setIsConnected] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [connectionStatus, setConnectionStatus] = useState<boolean>(false)

  console.log('📡 Hook state after initialization - user:', user)

  const hasInitialized = useRef(false)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

  // Convert WebSocket data to our format
  const transformDeviceData = useCallback((deviceId: string, data: DeviceDataFromWebSocket): RealtimeDeviceData => {
    return {
      deviceId,
      timestamp: data.timestamp,

      // Primary data fields
      dp0: data.dp0,
      dp1: data.dp1,

      // Threshold configuration
      dp0wt: data.dp0wt,
      dp1wt: data.dp1wt,
      dp0at: data.dp0at,
      dp1at: data.dp1at,

      // Alarm states
      dp0_alarm_state: data.dp0_alarm_state,
      dp1_alarm_state: data.dp1_alarm_state,

      // Connection status
      w_rssi: data.w_rssi,
      c_rssi: data.c_rssi,
      w_connected: data.w_connected,
      c_connected: data.c_connected,

      // Power status
      on_batt: data.on_batt,
      on_power: data.on_power,
      batt_percent: data.batt_percent,

      // Device configuration
      zone1_name: data.zone1_name,
      zone2_name: data.zone2_name,
      measure_unit: data.measure_unit,
      measure_sec: data.measure_sec,
      auto_z_sec: data.auto_z_sec,
      uptime_sec: data.uptime_sec,

      // Bridge connectivity
      br_ping_ct: data.br_ping_ct,
      br_bad_ping_ct: data.br_bad_ping_ct,
      br_bad_ping_seq_ct: data.br_bad_ping_seq_ct,
      br_longest_bad_ping_seq_ct: data.br_longest_bad_ping_seq_ct,

      rawData: data
    }
  }, [])

  // Handle device data updates from WebSocket
  const handleDeviceDataUpdate = useCallback((deviceId: string, data: DeviceDataFromWebSocket, events: any[]) => {
    console.log(`📡 Received device data for ${deviceId}:`, data)

    const transformedData = transformDeviceData(deviceId, data)

    // Update local Map for backwards compatibility with existing components
    setDeviceData(prevData => {
      const newData = new Map(prevData)
      newData.set(deviceId, transformedData)
      return newData
    })

    // Update global store - this will trigger re-renders in all subscribed components
    updateRealtimeDeviceData(deviceId, {
      timestamp: data.timestamp,
      dp0: data.dp0,
      dp1: data.dp1,
      dp0wt: data.dp0wt,
      dp1wt: data.dp1wt,
      dp0at: data.dp0at,
      dp1at: data.dp1at,
      dp0_alarm_state: data.dp0_alarm_state,
      dp1_alarm_state: data.dp1_alarm_state,
      w_rssi: data.w_rssi,
      c_rssi: data.c_rssi,
      measure_unit: data.measure_unit,
      br_ping_ct: data.br_ping_ct,
      br_bad_ping_ct: data.br_bad_ping_ct,
      br_bad_ping_seq_ct: data.br_bad_ping_seq_ct,
      br_longest_bad_ping_seq_ct: data.br_longest_bad_ping_seq_ct
    })

    // Log any events if present
    if (events && events.length > 0) {
      console.log(`📡 Device events for ${deviceId}:`, events)
    }
  }, [transformDeviceData, updateRealtimeDeviceData])

  // Handle connection state changes
  const handleConnectionStateChange = useCallback((connected: boolean) => {
    console.log(`📡 WebSocket connection state changed:`, connected)
    setIsConnected(connected)
    setIsConnecting(false)
    
    if (connected) {
      setError(null)
    }
    
    // Update connection status
    setConnectionStatus(webSocketService.getConnectionStatus())
  }, [])

  // Connect to WebSocket
  const connect = useCallback(async () => {
    // Check if realtime features are enabled
    const realtimeEnabled = import.meta.env.VITE_STRATA47_ENABLE_REALTIME_FEATURES === 'true'
    if (!realtimeEnabled) {
      console.log('📡 Realtime features disabled, skipping WebSocket connection')
      return
    }
    
    if (isConnecting || isConnected) {
      console.log('📡 WebSocket already connecting or connected, skipping...')
      return
    }
    
    try {
      setIsConnecting(true)
      setError(null)
      
      console.log('📡 Getting access token for WebSocket connection...')
      console.log('📡 User data:', user)
      console.log('📡 User namespace:', user?.namespace)
      
      const token = await getAccessToken()
      
      if (!token) {
        throw new Error('No access token available')
      }
      
      if (!user) {
        throw new Error('User not available - authentication may not be complete')
      }
      
      if (!user.namespace?.id) {
        console.error('📡 User namespace data:', user.namespace)
        throw new Error('No user namespace ID available from Strata47 API. Cannot connect to WebSocket without valid namespace.')
      }
      
      console.log('📡 Connecting to WebSocket with token:', token.substring(0, 20) + '...')
      console.log('📡 Using namespace ID as agent ID:', user.namespace.id)
      
      // Use user's namespace name for connection  
      const namespace = user.namespace.name
      
      // Set up event handlers
      webSocketService.onDeviceData(handleDeviceDataUpdate)
      webSocketService.onConnectionChange(handleConnectionStateChange)
      webSocketService.onErrorOccurred((wsError: Error) => {
        console.error(`📡 WebSocket error:`, wsError)
        setError(wsError.message)
        setIsConnecting(false)
        
        // Check if realtime features are enabled before auto-reconnecting
        const realtimeEnabled = import.meta.env.VITE_STRATA47_ENABLE_REALTIME_FEATURES === 'true'
        if (!realtimeEnabled) {
          console.log('📡 Realtime features disabled, not attempting reconnection')
          return
        }
        
        // Auto-reconnect after delay with exponential backoff
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current)
        }
        
        const reconnectDelay = Math.min(5000 * Math.pow(2, Math.random() * 3), 30000) // Random backoff up to 30s
        console.log(`📡 Attempting WebSocket reconnection in ${reconnectDelay}ms...`)
        
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('📡 Reconnecting WebSocket...')
          connect()
        }, reconnectDelay)
      })
      
      // Connect using user's namespace ID as agent ID
      webSocketService.connect(namespace, `auth0|${token}`, user.namespace.id)
      
    } catch (err) {
      console.error('📡 Failed to connect to WebSocket:', err)
      setError(err instanceof Error ? err.message : 'Unknown connection error')
      setIsConnecting(false)
    }
  }, [getAccessToken, user, handleDeviceDataUpdate, handleConnectionStateChange, isConnecting, isConnected])

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    console.log('📡 Disconnecting from WebSocket...')
    setIsConnecting(false)
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = undefined
    }
    
    webSocketService.disconnect()
  }, [])

  // Get device data for a specific device
  const getDeviceData = useCallback((deviceId: string): RealtimeDeviceData | undefined => {
    return deviceData.get(deviceId)
  }, [deviceData])

  // Auto-connect when user namespace becomes available
  useEffect(() => {
    console.log('📡 ==================== WEBSOCKET INIT DEBUG ====================')
    console.log('📡 Hook useEffect running...')
    console.log('📡 hasInitialized.current:', hasInitialized.current)
    console.log('📡 user object:', user)
    console.log('📡 user?.namespace:', user?.namespace)
    console.log('📡 user?.namespace?.id:', user?.namespace?.id)
    console.log('📡 Realtime features enabled:', import.meta.env.VITE_STRATA47_ENABLE_REALTIME_FEATURES)
    console.log('📡 isConnecting:', isConnecting)
    console.log('📡 isConnected:', isConnected)

    if (!hasInitialized.current && user?.namespace?.id) {
      hasInitialized.current = true
      console.log('📡 ✅ All conditions met - Initializing WebSocket connection...')
      connect()
    } else {
      console.log('📡 ❌ Conditions not met for WebSocket initialization:')
      if (hasInitialized.current) {
        console.log('📡   - Already initialized')
      }
      if (!user) {
        console.log('📡   - User object is null/undefined')
      }
      if (user && !user.namespace) {
        console.log('📡   - User namespace is null/undefined')
      }
      if (user?.namespace && !user.namespace.id) {
        console.log('📡   - User namespace ID is null/undefined')
      }
    }
    console.log('📡 ==============================================================')
  }, [connect, user?.namespace?.id, user, isConnecting, isConnected])
  
  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      console.log('📡 Cleaning up WebSocket connection on unmount...')
      disconnect()
    }
  }, [disconnect])

  // Cleanup reconnect timeout on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
    }
  }, [])

  return {
    deviceData,
    isConnected,
    isConnecting,
    error,
    connectionStatus,
    connect,
    disconnect,
    getDeviceData
  }
}